import React, { useEffect, useState, useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';

import { templateService } from '../../services/templateService';
import { userInformationAtom } from '../../../../Services/_states/auth.js';
import { radiusPx } from '../../../../theme/tokens';

export default function TemplateLiveBar({
  template,
  templateName = '',
  onClose,
}) {
  const loggedInUser = useRecoilValue(userInformationAtom);
  const loggedInOrgId = loggedInUser?.profile?.organisation?.organisation_id || '';
  const companyId = template?.company_id || loggedInOrgId;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchHealth = useCallback(async () => {
    // Primary identifier: wa_Id / wa_id as specified
    const effectiveId =
      template?.wa_Id ||
      template?.wa_id ||
      template?.template_id ||
      template?.id ||
      templateName ||
      template?.name ||
      '';

    if (!effectiveId) return;

    setLoading(true);
    setError(null);

    try {
      const res = await templateService.getTrustSignalTemplateHealth({
        templateId: effectiveId,
        name: template?.name || templateName,
        wabaNumber: template?.bot_number || '',
        companyId,
      });

      const raw = res?.data?.data ?? res?.data ?? {};
      const qualityScoreObj = raw.quality_score || {};
      const qualityScore =
        typeof qualityScoreObj === 'object'
          ? qualityScoreObj.score || 'UNKNOWN'
          : String(raw.quality_score || 'UNKNOWN');

      setData({
        id: raw.id || effectiveId,
        name: raw.name || template?.name || templateName,
        status: raw.status || 'APPROVED',
        category: raw.category || template?.category || '',
        language: raw.language || template?.language || '',
        qualityScore,
        rejectionReason:
          raw.rejected_reason && raw.rejected_reason !== 'NONE'
            ? raw.rejected_reason
            : '',
      });
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        err?.message ||
        'Failed to check template health';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [template, templateName, companyId]);

  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  const normQuality = String(data?.qualityScore || 'UNKNOWN').toUpperCase();
  const isGreen = normQuality.includes('GREEN') || normQuality === 'HIGH' || normQuality === 'GOOD';
  const isYellow = normQuality.includes('YELLOW') || normQuality === 'MEDIUM';
  const isRed = normQuality.includes('RED') || normQuality === 'LOW';

  const normStatus = String(data?.status || template?.status || 'APPROVED').toUpperCase();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1.5,
        p: 1.25,
        px: 2,
        mt: 1.5,
        borderRadius: radiusPx.md,
        bgcolor: '#f8fafc',
        border: '1px solid #e2e8f0',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            color: '#059669',
            fontWeight: 700,
            fontSize: '0.8125rem',
          }}
        >
          <MonitorHeartIcon sx={{ fontSize: 18 }} />
          <span>Template Health:</span>
        </Box>

        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
          {template?.name || templateName}
        </Typography>

        {loading ? (
          <CircularProgress size={14} sx={{ color: '#059669' }} />
        ) : error ? (
          <Chip
            size="small"
            label={error}
            sx={{
              bgcolor: '#ffebee',
              color: '#d32f2f',
              fontSize: '0.7rem',
              height: 22,
              fontWeight: 600,
            }}
          />
        ) : data ? (
          <>
            <Chip
              size="small"
              icon={
                isGreen ? (
                  <CheckCircleIcon sx={{ fontSize: 14 }} />
                ) : isYellow ? (
                  <WarningAmberIcon sx={{ fontSize: 14 }} />
                ) : isRed ? (
                  <ErrorOutlineIcon sx={{ fontSize: 14 }} />
                ) : (
                  <HelpOutlineIcon sx={{ fontSize: 14 }} />
                )
              }
              label={`Quality: ${normQuality}`}
              sx={{
                bgcolor: isGreen ? '#e8f5e9' : isYellow ? '#fff8e1' : isRed ? '#ffebee' : '#f5f5f5',
                color: isGreen ? '#2e7d32' : isYellow ? '#ed6c02' : isRed ? '#d32f2f' : '#616161',
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 22,
              }}
            />
            <Chip
              size="small"
              label={normStatus}
              sx={{
                bgcolor: normStatus === 'APPROVED' ? '#ecfdf5' : '#fef3c7',
                color: normStatus === 'APPROVED' ? '#065f46' : '#92400e',
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 22,
              }}
            />
            {data.category && (
              <Chip
                size="small"
                label={`Category: ${data.category}`}
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 22 }}
              />
            )}
            {data.language && (
              <Chip
                size="small"
                label={`Lang: ${data.language}`}
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 22 }}
              />
            )}
            {data.rejectionReason && (
              <Chip
                size="small"
                label={`Reason: ${data.rejectionReason}`}
                sx={{ bgcolor: '#ffebee', color: '#d32f2f', fontSize: '0.7rem', height: 22 }}
              />
            )}
          </>
        ) : null}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Tooltip title="Refresh template health" arrow>
          <span>
            <IconButton
              size="small"
              onClick={fetchHealth}
              disabled={loading}
              sx={{ p: 0.5 }}
            >
              <RefreshIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>

        {onClose && (
          <Tooltip title="Close health bar" arrow>
            <IconButton size="small" onClick={onClose} sx={{ p: 0.5 }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}

